import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { AreaChart, Area, ResponsiveContainer, Tooltip } from "recharts";
import api from "@/lib/api";
import { Radio, Users, ShoppingCart, TrendingUp, RefreshCw, CreditCard, Inbox } from "lucide-react";

function Counter({ value, prefix = "", suffix = "", decimals = 0 }) {
  const [display, setDisplay] = useState(value);
  const from = useRef(value);
  useEffect(() => {
    const start = from.current; const end = value; const t0 = Date.now(); const dur = 800;
    let raf;
    const tick = () => {
      const p = Math.min((Date.now() - t0) / dur, 1);
      setDisplay(start + (end - start) * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick); else from.current = end;
    };
    tick();
    return () => cancelAnimationFrame(raf);
  }, [value]);
  return <span>{prefix}{display.toLocaleString("fr-FR", { minimumFractionDigits: decimals, maximumFractionDigits: decimals })}{suffix}</span>;
}

export default function LiveSales() {
  const [data, setData] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const timer = useRef(null);

  const fetchData = async () => {
    setRefreshing(true);
    try { const r = await api.get("/analytics/live-sales"); setData(r.data); } catch { /* noop */ }
    setTimeout(() => setRefreshing(false), 400);
  };
  useEffect(() => {
    fetchData();
    timer.current = setInterval(fetchData, 8000);
    return () => clearInterval(timer.current);
  }, []);

  if (!data) return (
    <div className="p-6 rounded-2xl border border-border bg-card h-72 flex items-center justify-center">
      <div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" />
    </div>
  );

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid="live-sales">
      <div className="p-5 border-b border-border flex items-center justify-between bg-gradient-to-r from-indigo-500/10 to-transparent">
        <div className="flex items-center gap-2">
          <span className="relative flex h-3 w-3">
            <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-500 opacity-60 animate-ping" />
            <span className="relative inline-flex h-3 w-3 rounded-full bg-emerald-500" />
          </span>
          <div>
            <h3 className="font-display font-bold text-lg flex items-center gap-2"><Radio size={18} className="text-primary" /> Ventes en direct</h3>
            <p className="text-xs text-muted-foreground">Abonnements Monaga · Stripe (données réelles)</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-muted-foreground font-mono whitespace-nowrap">maj {data.updated_at}</span>
          <button onClick={fetchData} className="h-8 w-8 rounded-lg hover:bg-secondary flex items-center justify-center" title="Actualiser" data-testid="live-refresh-btn">
            <RefreshCw size={15} className={refreshing ? "animate-spin text-primary" : "text-muted-foreground"} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 [&>div]:border-border [&>div:nth-child(odd)]:border-r sm:[&>div]:border-r [&>div:nth-child(-n+2)]:border-b sm:[&>div]:border-b-0 sm:[&>div:last-child]:border-r-0">
        <div className="p-5" data-testid="live-total-revenue">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><TrendingUp size={13} className="text-emerald-500" /> CA encaissé</div>
          <p className="font-display text-2xl font-extrabold"><Counter value={data.total_revenue} suffix=" €" decimals={0} /></p>
          <p className="text-[11px] text-muted-foreground mt-0.5">dont {data.today_revenue.toLocaleString("fr-FR")} € aujourd'hui</p>
        </div>
        <div className="p-5" data-testid="live-total-orders">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><ShoppingCart size={13} className="text-indigo-400" /> Abonnements payés</div>
          <p className="font-display text-2xl font-extrabold"><Counter value={data.total_orders} /></p>
          <p className="text-[11px] text-muted-foreground mt-0.5">{data.today_orders} aujourd'hui</p>
        </div>
        <div className="p-5" data-testid="live-subscribers">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><Users size={13} className="text-cyan-400" /> Abonnés actifs</div>
          <p className="font-display text-2xl font-extrabold text-emerald-500"><Counter value={data.active_subscribers} /></p>
        </div>
        <div className="p-5" data-testid="live-conversion">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-1"><TrendingUp size={13} className="text-pink-400" /> Taux conv.</div>
          <p className="font-display text-2xl font-extrabold"><Counter value={data.conversion_rate} suffix=" %" decimals={1} /></p>
        </div>
      </div>

      <div className="h-24 px-2">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data.hourly}>
            <defs><linearGradient id="live" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#10b981" stopOpacity={0.5} /><stop offset="100%" stopColor="#10b981" stopOpacity={0} /></linearGradient></defs>
            <Tooltip contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12 }} formatter={(v) => [`${Math.round(v)} €`, "CA"]} labelFormatter={(l) => `À ${l}`} />
            <Area type="monotone" dataKey="revenue" stroke="#10b981" strokeWidth={2} fill="url(#live)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="p-4 border-t border-border">
        <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Derniers abonnements</p>
        {data.is_empty ? (
          <div className="flex flex-col items-center justify-center py-8 text-center" data-testid="live-empty">
            <Inbox size={28} className="text-muted-foreground mb-2" />
            <p className="text-sm font-medium">Aucune vente pour l'instant</p>
            <p className="text-xs text-muted-foreground mt-1">Vos abonnements Stripe payés apparaîtront ici en temps réel.</p>
          </div>
        ) : (
          <div className="space-y-1.5 max-h-56 overflow-hidden">
            <AnimatePresence initial={false}>
              {data.orders.map((o) => (
                <motion.div
                  key={o.id}
                  layout
                  initial={{ opacity: 0, y: -10, backgroundColor: "rgba(16,185,129,0.12)" }}
                  animate={{ opacity: 1, y: 0, backgroundColor: "rgba(0,0,0,0)" }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                  className="flex items-center gap-3 p-2 rounded-xl"
                  data-testid={`live-order-${o.id}`}
                >
                  <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shrink-0">
                    <CreditCard size={15} className="text-white" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium truncate">{o.label}</p>
                    <p className="text-xs text-muted-foreground">{o.time}</p>
                  </div>
                  <span className="font-display font-bold text-emerald-500 text-sm shrink-0">+{o.amount.toLocaleString("fr-FR")} €</span>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
}
